import { useConnectionState, useRemoteParticipants } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { useEffect, useState } from 'react';

/**
 * Whether the doctor has actually dropped out of the call.
 *
 * Presence comes from the LiveKit room and nothing else. This used to listen to
 * `telemedicine:participant-left` on the socket, which rides a separate transport with
 * its own reconnect schedule — so a doctor whose socket merely blinked (backgrounded
 * tab, network handover) was reported as gone while their media session, and their
 * video, never dropped. Worse, the flag was only ever cleared by a matching
 * `participant-joined`: if the doctor's client did not re-announce itself, the patient
 * was told the doctor had disconnected for the rest of the consultation while the two
 * of them were talking normally. The room is the only thing that knows whether media
 * is still flowing.
 *
 * "Lost" is deliberately narrower than "absent": the doctor has to have been in the
 * room at some point before their absence means anything, otherwise the patient would
 * be told the doctor disconnected before they had ever joined.
 */
const useDoctorPresence = () => {
	const remoteParticipants = useRemoteParticipants();
	const connectionState = useConnectionState();

	const [hasDoctorJoined, setHasDoctorJoined] = useState(false);

	const isDoctorInRoom = remoteParticipants.length > 0;

	useEffect(() => {
		if (isDoctorInRoom) {
			setHasDoctorJoined(true);
		}
	}, [isDoctorInRoom]);

	// While our own connection is re-establishing, the participant list is stale and says
	// nothing about the doctor — only about us.
	const isSelfReconnecting = connectionState !== ConnectionState.Connected;

	return { isDoctorConnectionLost: hasDoctorJoined && !isDoctorInRoom && !isSelfReconnecting };
};

export default useDoctorPresence;
